import React, { useEffect, useState, useRef } from "react";
import "./alerts.scss";
import {
	Checkbox,
	CheckPicker,
	Divider,
	SelectPicker,
	Button,
	Modal,
} from "rsuite";
import { Segmented, Table, Empty } from "antd";
import upRedArrow from "../../assets/red-up-arrow.svg";
import upGreenArrow from "../../assets/green-up-arrow.svg";
import { connect, useDispatch, useSelector } from "react-redux";
import {
	getCategoryPerformanceAlerts,
	getCategoryPerformanceAlertsFilters,
	getCategoryPerformanceAlertsOpportunity,
} from "../../queries/apiQueries";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { FILTERDATA } from "../../utils";
import MainContent from "../../components/Layouts/MainContent";
import MainHeader from "../../components/Layouts/MainHeader";
import ToggleSwitch from "../../components/common/ToggleSwitch";
import { Loader } from "../../components/common/Loader";
import { projectType, priority } from "../constants";
import { CSVLink } from "react-csv";
import FileDownloadIcon from "@rsuite/icons/FileDownload";

// Interfaces remain the same
interface DataForCalculationBase {
	cnt: number;
	change: number;
	toggle: {
		cnt: number;
		change: number;
	};
}
interface ProjectTypeOption {
	label: string;
	value: string;
}
interface MarginDataForCalculation extends DataForCalculationBase {
	cy: {
		cost: number;
		rev: number;
	};
	ly: {
		cost: number;
		rev: number;
	};
}
interface OtherDataForCalculation extends DataForCalculationBase {
	cy: number;
	ly: number;
}
interface SubcategoryData {
	margin?: {
		dataForCalculation: MarginDataForCalculation;
		data: Record<string, any>;
	};
	sales?: {
		dataForCalculation: OtherDataForCalculation;
		data: Record<string, any>;
	};
	sellprice?: {
		dataForCalculation: OtherDataForCalculation;
		data: Record<string, any>;
	};
	cost?: {
		dataForCalculation: OtherDataForCalculation;
		data: Record<string, any>;
	};
}
interface LevelData {
	category?: SubcategoryData;
	district?: SubcategoryData;
	supplier?: SubcategoryData;
}
interface ApiResponse {
	finalData: LevelData;
}
interface SubcategoryParsedData {
	cnt: number;
	change: number;
}
interface LevelParsedData {
	margin?: SubcategoryParsedData;
	sales?: SubcategoryParsedData;
	sellprice?: SubcategoryParsedData;
	cost?: SubcategoryParsedData;
}
interface ParsedData {
	category?: LevelParsedData;
	district?: LevelParsedData;
	supplier?: LevelParsedData;
}
interface Card {
	title: string;
	count: string | number;
	subtitle: string;
	amount: string | number;
	subamount: string;
	alert: boolean;
}
interface Menu {
	name: string;
	title: string;
	cards: Card[];
}

// Utility function to get SelectPicker data
const getSelectPickerData = () => {
	return projectType.map((option) => {
		if (Array.isArray(option.value)) {
			// Convert empty array to "All" for the SelectPicker
			return {
				...option,
				value: "All",
			};
		}
		return option;
	});
};

const formatNumber = (value: any): string => {
	if (value == null) return "-";
	if (typeof value !== "number") return String(value);
	const absValue = Math.abs(value);
	if (absValue < 1000) {
		return value.toFixed(2);
	}
	if (absValue < 1000000) {
		const rounded = Math.round(value / 1000);
		return rounded + "k";
	}
	if (absValue < 1000000000) {
		const rounded = Math.round(value / 1000000);
		return rounded + "M";
	}
	const rounded = Math.round(value / 1000000000);
	return rounded + "B";
};

// Template for alert menus
const alertMenusTemplate: Menu[] = [
	{
		name: "CategoryLevel",
		title: "Category Level",
		cards: [
			{
				title: "Margin Reduction",
				count: "",
				subtitle: "Categories",
				amount: "",
				subamount: "Margin Drop",
				alert: true,
			},
			{
				title: "Sales Reduction",
				count: "",
				subtitle: "Categories",
				amount: "",
				subamount: "Sales Drop",
				alert: true,
			},
			{
				title: "Sell Price Reduction",
				count: "",
				subtitle: "Categories",
				amount: "",
				subamount: "Sell Price Drop",
				alert: true,
			},
			{
				title: "Cost Increases",
				count: "",
				subtitle: "Categories",
				amount: "",
				subamount: "Cost Increases",
				alert: true,
			},
		],
	},
	{
		name: "SupplierLevel",
		title: "Supplier Level",
		cards: [
			{
				title: "Margin Reduction",
				count: "",
				subtitle: "Suppliers",
				amount: "",
				subamount: "Margin Drop",
				alert: true,
			},
			{
				title: "Sales Reduction",
				count: "",
				subtitle: "Suppliers",
				amount: "",
				subamount: "Sales Drop",
				alert: true,
			},
			{
				title: "Sell Price Reduction",
				count: "",
				subtitle: "Suppliers",
				amount: "",
				subamount: "Sell Price Drop",
				alert: true,
			},
			{
				title: "Cost Increases",
				count: "",
				subtitle: "Suppliers",
				amount: "",
				subamount: "Cost Increases",
				alert: true,
			},
		],
	},
	{
		name: "DistrictLevel",
		title: "District Level",
		cards: [
			{
				title: "Margin Reduction",
				count: "",
				subtitle: "Districts",
				amount: "",
				subamount: "Margin Drop",
				alert: true,
			},
			{
				title: "Sales Reduction",
				count: "",
				subtitle: "Districts",
				amount: "",
				subamount: "Sales Drop",
				alert: true,
			},
			{
				title: "Sell Price Reduction",
				count: "",
				subtitle: "Districts",
				amount: "",
				subamount: "Sell Price Drop",
				alert: true,
			},
			{
				title: "Cost Increases",
				count: "",
				subtitle: "Districts",
				amount: "",
				subamount: "Cost Increases",
				alert: true,
			},
		],
	},
];

const alertMenusTemplateImproved: Menu[] = [
	{
		name: "CategoryLevel",
		title: "Category Level",
		cards: [
			{
				title: "Margin Increases",
				count: "",
				subtitle: "Categories",
				amount: "",
				subamount: "Margin Improvement",
				alert: true,
			},
			{
				title: "Sales Increases",
				count: "",
				subtitle: "Categories",
				amount: "",
				subamount: "Sales Improvement",
				alert: true,
			},
			{
				title: "Sell Price Increases",
				count: "",
				subtitle: "Categories",
				amount: "",
				subamount: "Sell Price Improvement",
				alert: true,
			},
			{
				title: "Cost Reduction",
				count: "",
				subtitle: "Categories",
				amount: "",
				subamount: "Cost Reduction",
				alert: true,
			},
		],
	},
	{
		name: "SupplierLevel",
		title: "Supplier Level",
		cards: [
			{
				title: "Margin Increases",
				count: "",
				subtitle: "Suppliers",
				amount: "",
				subamount: "Margin Improvement",
				alert: true,
			},
			{
				title: "Sales Increases",
				count: "",
				subtitle: "Suppliers",
				amount: "",
				subamount: "Sales Improvement",
				alert: true,
			},
			{
				title: "Sell Price Increases",
				count: "",
				subtitle: "Suppliers",
				amount: "",
				subamount: "Sell Price Improvement",
				alert: true,
			},
			{
				title: "Cost Reduction",
				count: "",
				subtitle: "Suppliers",
				amount: "",
				subamount: "Cost Reduction",
				alert: true,
			},
		],
	},
	{
		name: "DistrictLevel",
		title: "District Level",
		cards: [
			{
				title: "Margin Increases",
				count: "",
				subtitle: "Districts",
				amount: "",
				subamount: "Margin Improvement",
				alert: true,
			},
			{
				title: "Sales Increases",
				count: "",
				subtitle: "Districts",
				amount: "",
				subamount: "Sales Improvement",
				alert: true,
			},
			{
				title: "Sell Price Increases",
				count: "",
				subtitle: "Districts",
				amount: "",
				subamount: "Sell Price Improvement",
				alert: true,
			},
			{
				title: "Cost Reduction",
				count: "",
				subtitle: "Districts",
				amount: "",
				subamount: "Cost Reduction",
				alert: true,
			},
		],
	},
];

// Function to parse API response
const parseApiResponse = (
	apiResponse: ApiResponse,
	handlePositiveNegative: boolean = false
): ParsedData => {
	const { finalData } = apiResponse;
	const result: ParsedData = {};
	(["category", "district", "supplier"] as const).forEach((level) => {
		if (!finalData[level]) return;
		result[level] = {};
		(["margin", "sales", "sellprice", "cost"] as const).forEach(
			(subcategory) => {
				if (!finalData[level]?.[subcategory]?.dataForCalculation)
					return;
				const dataForCalculation =
					finalData[level]![subcategory]!.dataForCalculation;
				let cnt: number;
				let change: number;
				if (subcategory === "margin") {
					cnt =
						(handlePositiveNegative
							? dataForCalculation?.toggle?.cnt
							: dataForCalculation.cnt) || 0;
					change =
						(handlePositiveNegative
							? dataForCalculation?.toggle?.change
							: dataForCalculation.change) || 0;
				} else {
					cnt =
						(handlePositiveNegative
							? dataForCalculation?.toggle?.cnt
							: dataForCalculation.cnt) || 0;
					change =
						(handlePositiveNegative
							? dataForCalculation?.toggle?.change
							: dataForCalculation.change) || 0;
				}
				result[level] = result[level] || {};
				(result[level] as LevelParsedData)[subcategory] = {
					cnt,
					change,
				};
			}
		);
	});
	return result;
};

// Main Alerts component
const Alerts = (props: any) => {
	const dispatch = useDispatch();
	const { accounts } = useMsal();
	const loggedInEmail = accounts[0]?.username;
	const [alertMenus, setAlertMenus] = useState<Menu[]>(alertMenusTemplate);
	const [managers, setManagers] = useState<
		{ label: string; value: string }[]
	>([]);
	const [selectedManager, setSelectedManager] = useState<string>("");
	const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalData, setModalData] = useState<Record<string, any> | null>(
		null
	);
	const [selectedPriority, setSelectedPriority] = useState(
		priority.length ? priority[2].value : 3
	);
	const [isVisible, setIsVisible] = useState(false);
	const [modalTitle, setModalTitle] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [clickedCardType, setClickedCardType] = useState<number | null>(null);
	const selectPickerProjectType = getSelectPickerData();
	const allOption = selectPickerProjectType.find(
		(option) => option.value === "All"
	);
	const [isRightVisible, setIsRightVisible] = useState(false);
	const [handlePositiveNegative, setHandlePositiveNegative] = useState(false);
	const initialProjectType = allOption
		? allOption.value
		: projectType.length > 0
		? typeof projectType[0].value === "string"
			? projectType[0].value
			: "All"
		: "";
	const [selectedProjectType, setSelectedProjectType] = useState<string>(
		initialProjectType as string
	);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const navigate = useNavigate();
	const [exportDatas, setexportDatas] = useState<any>([]);
	const [exportImprovedDatas, setExportImprovedDatas] = useState<any>([]);
	const exportFullRef = useRef<any>(null);

	const exportDataClick = () => {
		if (!handlePositiveNegative) {
			if (exportDatas?.length != 0) {
				exportFullRef?.current?.link.click();
			}
		} else {
			if (exportImprovedDatas?.length != 0) {
				exportFullRef?.current?.link.click();
			}
		}
	};

	const csvHeaders = [
		{ label: "Name", key: "name" },
		{ label: "Source", key: "source" },
		{ label: "Sales 2025", key: "CommonSales2024" },
		{ label: "Spend 2025", key: "CommonSpend2024" },
		{ label: "2026 Gross Margin (%) YTD", key: "gm2025" },
		{ label: "2025 Gross Margin (%)", key: "gm2024" },
		{
			label: `${handlePositiveNegative ? "% GM Increase" : "% GM Drop"}`,
			key: "gmDrop",
		},
		{ label: "Priority_% GM Change", key: "marginPriority" },
		{ label: "2026 Sales YTD($)", key: "sales2025" },
		{ label: "2025 sales YTD($)", key: "sales2024" },
		{
			label: `${
				handlePositiveNegative ? "% Sales Increase" : "% Sales Drop"
			}`,
			key: "salesDrop",
		},
		{ label: "Priority_% Sales Change", key: "salesPriority" },
		{ label: "2026 YTD Sell Price($/unit)", key: "sellPrice2025" },
		{ label: "2025 Sell Price($/unit)", key: "sellPrice2024" },
		{
			label: `${
				handlePositiveNegative
					? "% Sell Price Increase"
					: "% Sell Price Drop"
			}`,
			key: "sellPriceDrop",
		},
		{ label: "Priority_% Sell Price Change", key: "sellpricePriority" },
		{ label: "2026 Net Cost YTD($)", key: "cost2025" },
		{ label: "2025 Net Cost YTD($)", key: "cost2024" },
		{
			label: `${
				handlePositiveNegative ? "% Cost Drop" : "% Cost Increase"
			}`,
			key: "costIncrease",
		},
		{ label: "Priority_% Cost Change", key: "costPriority" },
	];

	const handleRowClick = (categoryName: string) => {
		const level = modalTitle;
		let filter: any = {
			district: [],
			l1: [],
			l2: [],
			l3: [],
			supplier: [],
			projectType: [],
			type: null,
		};
		if (level === "district") {
			let data: any =
				apiResponse?.finalData["category"]?.["margin"]?.data;
			filter.district = [categoryName];
			filter.l3 = data ? Object.keys(data) : [];
			filter.projectType =
				selectedProjectType === "All" ? [] : selectedProjectType;
			filter.type = level;
		} else if (level === "supplier") {
			let data: any =
				apiResponse?.finalData["category"]?.["margin"]?.data;
			filter.supplier = [categoryName];
			filter.l3 = data ? Object.keys(data) : [];
			filter.projectType =
				selectedProjectType === "All" ? [] : selectedProjectType;
			filter.type = level;
		} else if (level === "category") {
			filter.l3 = [categoryName];
			filter.projectType =
				selectedProjectType === "All" ? [] : selectedProjectType;
			filter.type = level;
		}
		props.onFilterChange(filter);
		navigate("/category-performance", {});
	};
	const priorityHandler = (e: any) => {
		setSelectedPriority(e === null ? "All" : e);
		generateTableData(modalData, clickedCardType || 0);
	};

	const handleProjectTypeChange = (value: string | null) => {
		setSelectedProjectType(value || "");
	};

	useEffect(() => {
		getCategoryPerformanceAlertsFilters({}).then((response: any) => {
			const mangers = response?.data?.mangers || [];
			const managerOptions = [
				{
					label: "All",
					value: "",
					emailId: "",
				},
				...mangers
					.map((mgr: any) => ({
						label: mgr.manager,
						value: mgr.manager,
						emailId: mgr.emailId,
					}))
					.sort((a: { label: string }, b: { label: any }) =>
						a.label.localeCompare(b.label)
					),
			];
			setManagers(managerOptions);
			// Default to manager matching logged-in email, else keep initial empty string (which shows all)
			const matchedManager = managerOptions.find(
				(mgr: { emailId: string }) =>
					mgr.emailId?.toLowerCase() === loggedInEmail.toLowerCase()
			);
			if (matchedManager) {
				setSelectedManager(matchedManager.value);
			}
		});
	}, []);

	useEffect(() => {
		const isAllSelected =
			selectedProjectType === "All" ||
			selectedProjectType === "" ||
			!selectedProjectType;
		// Find the actual value to send to the API
		let projectTypeValue: string | string[] = [];
		if (!isAllSelected) {
			const selectedItem = projectType.find((option) => {
				if (Array.isArray(option.value)) {
					return false; // Skip the "All" option in this case
				}
				return option.value === selectedProjectType;
			});
			projectTypeValue = selectedItem ? selectedItem.value : "";
		}
		const payload = {
			manager: selectedManager ? [selectedManager] : [],
			projectType: isAllSelected ? [] : projectTypeValue,
		};
		setIsLoading(true);
		(dispatch as any)(getCategoryPerformanceAlerts(payload)).then(
			(response: ApiResponse) => {
				setApiResponse(response); // Store the full response
				const parsedData = parseApiResponse(
					response,
					handlePositiveNegative
				);
				setIsLoading(false);
				updateAlertMenus(parsedData);
			}
		);
	}, [dispatch, selectedProjectType, selectedManager]);

	useEffect(() => {
		if (apiResponse) {
			const parsedData = parseApiResponse(
				apiResponse,
				handlePositiveNegative
			);
			setIsLoading(false);
			updateAlertMenus(parsedData);
		}
	}, [handlePositiveNegative]);
	useEffect(() => {
		if (isModalOpen) {
			// Prevent background scroll when modal is open
			document.body.style.overflow = "hidden";
		} else {
			// Restore scroll when modal is closed
			document.body.style.overflow = "";
		}
		// Cleanup in case component unmounts while modal is open
		return () => {
			document.body.style.overflow = "";
		};
	}, [isModalOpen]);
	useEffect(() => {
		if (apiResponse) {
			getExportData();
		}
	}, [apiResponse, handlePositiveNegative]);
	const generateTableData = (
		data: Record<string, any> | null,
		cardType: number
	) => {
		if (!data) return [];
		// Create the array of row objects
		const rows = Object.entries(data).map(([categoryName, entry]) => {
			const spend2024 = entry.values?.cost_ly || "-";
			const sales2024 = entry.values?.sales_ly || "-";
			const opportunity = entry.values?.opportunity || "-";
			const valueCY = entry.values?.value_CY || 0;
			const valueLY = entry.values?.value_LY || 0;
			const priority = entry.values?.priority;
			const baseData = {
				categoryName,
				spend_2024: spend2024,
				sales_2024: sales2024,
				opportunity: opportunity,
				flag: entry?.flag,
			};
			switch (cardType) {
				case 0: // Margin Reduction
					return {
						...baseData,
						grossMargin2025: valueCY * 100,
						grossMargin2024: valueLY * 100,
						percentDrop: (valueLY - valueCY) * 100,
						priority: priority,
						rawData: entry,
					};
				case 1: // Sales Reduction
					const salesDrop =
						valueLY !== 0 ? (valueLY - valueCY) / valueLY : 0;
					return {
						...baseData,
						sales2025: valueCY,
						sales2024: valueLY,
						percentDrop: salesDrop,
						priority: priority,
						rawData: entry,
					};
				case 2: // Sell Price Reduction
					let priceDrop =
						valueLY !== 0 ? (valueLY - valueCY) / valueLY : 0;
					if (priceDrop < 0) priceDrop = Math.abs(priceDrop);
					return {
						...baseData,
						sellPrice2025: valueCY,
						sellPrice2024: valueLY,
						percentDrop: priceDrop,
						priority: priority,
						rawData: entry,
					};
				case 3: // Cost Increase
					const percentIncrease =
						valueLY !== 0 ? (valueCY - valueLY) / valueLY : 0;
					return {
						...baseData,
						cost2025: valueCY,
						cost2024: valueLY,
						percentIncrease: percentIncrease,
						priority: priority,
						rawData: entry,
					};
				default:
					return {
						...baseData,
						value_CY: valueCY,
						value_LY: valueLY,
						priority: priority,
						rawData: entry,
					};
			}
		});

		handlePositiveNegative
			? rows.sort((a, b) => {
					const aSpend =
						a.spend_2024 === "-" ? 0 : Number(a.spend_2024);
					const bSpend =
						b.spend_2024 === "-" ? 0 : Number(b.spend_2024);

					const numA = isNaN(aSpend) ? 0 : aSpend;
					const numB = isNaN(bSpend) ? 0 : bSpend;
					return numB - numA; // For descending order
			  })
			: rows.sort((a, b) => {
					const aOpportunity =
						a.opportunity === "-" ? 0 : Number(a.opportunity);
					const bOpportunity =
						b.opportunity === "-" ? 0 : Number(b.opportunity);

					const numA = isNaN(aOpportunity) ? 0 : aOpportunity;
					const numB = isNaN(bOpportunity) ? 0 : bOpportunity;
					return numB - numA; // For descending order
			  });

		if (selectedPriority != 3) {
			rows.sort((a, b) => {
				const aPriority = Number(a.priority);
				const bPriority = Number(b.priority);

				return bPriority - aPriority; // For descending order
			});
			let newData: any = [];
			rows.map((item: any) => {
				if (item.priority == selectedPriority) {
					newData.push(item);
				}
			});

			return newData;
		} else {
			return rows;
		}
	};

	const getExportData = () => {
		if (apiResponse) {
			const levels = ["category", "supplier", "district"];
			const subcategories = ["margin", "sales", "sellprice", "cost"];
			let exportData: any = {
				category: [],
				supplier: [],
				district: [],
			};
			let overallData: any = JSON.parse(
				JSON.stringify(apiResponse.finalData)
			);

			levels.map((level: any, index: any) => {
				let baseData: any = {};
				subcategories.map((sub: any, ind: any) => {
					Object.entries(overallData[level][sub].data).map(
						([categoryName, entry]: any) => {
							const spend2024 = entry.values?.cost_ly || "-";
							const sales2024 = entry.values?.sales_ly || "-";
							const opportunity =
								entry.values?.opportunity || "-";
							const valueCY = entry.values?.value_CY || 0;
							const valueLY = entry.values?.value_LY || 0;
							const priority = entry.values?.priority;
							// const flag = entry.flag;
							let current: any = {
								category: categoryName,
								common_spend_2024: spend2024,
								common_sales_2024: sales2024,
								opportunity: opportunity,
								// flag: entry.flag,
							};
							if (sub == "margin") {
								current = {
									...current,
									grossMargin2025: valueCY * 100,
									grossMargin2024: valueLY * 100,
									marginPercentDrop:
										(valueLY - valueCY) * 100,
									marginFlag: entry.flag,
									marginPriority: priority,
								};
							} else if (sub == "sales") {
								const salesDrop =
									valueLY !== 0
										? (valueLY - valueCY) / valueLY
										: 0;
								current = {
									...current,
									sales2025: valueCY,
									sales2024: valueLY,
									salesPercentDrop: salesDrop,
									salesPriority: priority,
									salesFlag: entry.flag,
								};
							} else if (sub == "sellprice") {
								let priceDrop =
									valueLY !== 0
										? (valueLY - valueCY) / valueLY
										: 0;
								current = {
									...current,
									sellPrice2025: valueCY,
									sellPrice2024: valueLY,
									sellpricePercentDrop: priceDrop,
									sellpricePriority: priority,
									sellpriceFlag: entry.flag,
								};
							} else if (sub == "cost") {
								const percentIncrease =
									valueLY !== 0
										? (valueCY - valueLY) / valueLY
										: 0;
								current = {
									...current,
									cost2025: valueCY,
									cost2024: valueLY,
									costPercentIncrease: percentIncrease,
									costPriority: priority,
									costFlag: entry.flag,
								};
							}
							baseData[categoryName] = {
								...baseData[categoryName],
								...current,
							};
						}
					);
				});
				exportData[level] = { ...baseData };
			});

			let finalExportData: any = [];
			let finalExportData1: any = [];

			Object.keys(exportData).map((level: any) => {
				Object.entries(exportData[level]).map(([name, entry]: any) => {
					if (
						!handlePositiveNegative &&
						(entry.sellpriceFlag ||
							entry.costFlag ||
							entry.marginFlag ||
							entry.salesFlag)
						// || (handlePositiveNegative &&
						// (!entry.sellpriceFlag || !entry.costFlag || !entry.marginFlag || !entry.salesFlag))
					) {
						finalExportData.push({
							name: entry.category,
							source: level,
							CommonSales2024: `$${(Number(entry.common_sales_2024) || 0).toFixed(2)}`,
							CommonSpend2024: `$${(Number(entry.common_spend_2024) || 0).toFixed(2)}`,
							gm2025: entry.marginFlag
								? `${entry.grossMargin2025.toFixed(2)}%`
								: "No Drop",
							gm2024: entry.marginFlag
								? `${entry.grossMargin2024.toFixed(2)}%`
								: "No Drop",
							gmDrop: entry.marginFlag
								? `${entry.marginPercentDrop.toFixed(2)}%`
								: "No Drop",
							marginPriority: entry.marginFlag
								? entry.marginPriority
								: "No Drop",
							sales2025: entry.salesFlag
								? `$${entry.sales2025.toFixed(2)}`
								: "No Drop",
							sales2024: entry.salesFlag
								? `$${entry.sales2024.toFixed(2)}`
								: "No Drop",
							salesDrop: entry.salesFlag
								? `${entry.salesPercentDrop.toFixed(2)}%`
								: "No Drop",
							salesPriority: entry.salesFlag
								? entry.salesPriority
								: "No Drop",
							sellPrice2025: entry.sellpriceFlag
								? `$${entry.sellPrice2025.toFixed(2)}`
								: "No Drop",
							sellPrice2024: entry.sellpriceFlag
								? `$${entry.sellPrice2024.toFixed(2)}`
								: "No Drop",
							sellPriceDrop: entry.sellpriceFlag
								? `${entry.sellpricePercentDrop.toFixed(2)}%`
								: "No Drop",
							sellpricePriority: entry.sellpriceFlag
								? entry.sellpricePriority
								: "No Drop",
							cost2025: entry.costFlag
								? `$${entry.cost2025.toFixed(2)}`
								: "No Increase",
							cost2024: entry.costFlag
								? `$${entry.cost2024.toFixed(2)}`
								: "No Increase",
							costIncrease: entry.costFlag
								? `${entry.costPercentIncrease.toFixed(2)}%`
								: "No Increase",
							costPriority: entry.costFlag
								? entry.costPriority
								: "No Increase",
							marginFlag: entry.marginFlag,
							salesFlag: entry.salesFlag,
							sellpriceFlag: entry.sellpriceFlag,
							costFlag: entry.costFlag,
						});
					}
					if (
						handlePositiveNegative &&
						(!entry.sellpriceFlag ||
							!entry.costFlag ||
							!entry.marginFlag ||
							!entry.salesFlag)
					) {
						finalExportData1.push({
							name: entry.category,
							source: level,
							CommonSales2024: `$${(Number(entry?.common_sales_2024) || 0).toFixed(2)}`,
							CommonSpend2024: `$${(Number(entry?.common_spend_2024) || 0).toFixed(2)}`,
							gm2025: !entry.marginFlag
								? `${entry.grossMargin2025.toFixed(2)}%`
								: "No Improvement",
							gm2024: !entry.marginFlag
								? `${entry.grossMargin2024.toFixed(2)}%`
								: "No Improvement",
							gmDrop: !entry.marginFlag
								? `${entry.marginPercentDrop.toFixed(2)}%`
								: "No Improvement",
							marginPriority: !entry.marginFlag
								? entry.marginPriority
								: "No Improvement",
							sales2025: !entry.salesFlag
								? `$${entry.sales2025.toFixed(2)}`
								: "No Improvement",
							sales2024: !entry.salesFlag
								? `$${entry.sales2024.toFixed(2)}`
								: "No Improvement",
							salesDrop: !entry.salesFlag
								? `${entry.salesPercentDrop.toFixed(2)}%`
								: "No Improvement",
							salesPriority: !entry.salesFlag
								? entry.salesPriority
								: "No Improvement",
							sellPrice2025: !entry.sellpriceFlag
								? `$${entry.sellPrice2025.toFixed(2)}`
								: "No Improvement",
							sellPrice2024: !entry.sellpriceFlag
								? `$${entry.sellPrice2024.toFixed(2)}`
								: "No Improvement",
							sellPriceDrop: !entry.sellpriceFlag
								? `${entry.sellpricePercentDrop.toFixed(2)}%`
								: "No Improvement",
							sellpricePriority: !entry.sellpriceFlag
								? entry.sellpricePriority
								: "No Improvement",
							cost2025: !entry.costFlag
								? `$${entry.cost2025.toFixed(2)}`
								: "No Drop",
							cost2024: !entry.costFlag
								? `$${entry.cost2024.toFixed(2)}`
								: "No Drop",
							costIncrease: !entry.costFlag
								? `${entry.costPercentIncrease.toFixed(2)}%`
								: "No Drop",
							costPriority: !entry.costFlag
								? entry.costPriority
								: "No Drop",
							marginFlag: entry.marginFlag,
							salesFlag: entry.salesFlag,
							sellpriceFlag: entry.sellpriceFlag,
							costFlag: entry.costFlag,
						});
					}
				});
			});

			setexportDatas(finalExportData);
			setExportImprovedDatas(finalExportData1);
		}
	};

	const tableData = generateTableData(modalData, clickedCardType || 0);

	const handleCardClick = async (menuIndex: number, cardIndex: number) => {
		if (!apiResponse) return;
		const levels = ["category", "supplier", "district"] as const;
		const level = levels[menuIndex];
		const subcategories = ["margin", "sales", "sellprice", "cost"] as const;
		const subcategory = subcategories[cardIndex];
		const data = apiResponse.finalData[level]?.[subcategory]?.data;
		if (!data) return;
		setModalData({ ...data });
		setModalTitle(`${level}`);
		setClickedCardType(cardIndex);
		setIsModalOpen(true);
		if (level === "district") {
			return;
		}
		setIsLoading(true);
		const categoryNames = Object.keys(data);
		let payload: any = {};
		if (level === "category") {
			payload.l3 = categoryNames;
		} else if (level === "supplier") {
			payload.master_vendor_name = categoryNames;
		}
		try {
			const response = await (dispatch as any)(
				getCategoryPerformanceAlertsOpportunity(payload)
			);
			if (response && response.data) {
				const opportunityData = response.data;
				const aggregatedOpportunities: Record<string, number> = {};
				opportunityData.forEach((item: any) => {
					const { keyType, opportunity } = item;
					if (!aggregatedOpportunities[keyType]) {
						aggregatedOpportunities[keyType] = 0;
					}
					aggregatedOpportunities[keyType] += opportunity;
				});
				const updatedData = { ...data };
				Object.keys(updatedData).forEach((key) => {
					if (aggregatedOpportunities[key] !== undefined) {
						updatedData[key].opportunity =
							aggregatedOpportunities[key];
					} else {
						updatedData[key].opportunity = 0;
					}
				});
				setModalData(updatedData);
			}
		} catch (error) {
			console.error("Error fetching opportunity data:", error);
		} finally {
			setIsLoading(false);
		}
	};

	const updateAlertMenus = (parsedData: ParsedData) => {
		const updatedMenus = [
			...(handlePositiveNegative
				? alertMenusTemplateImproved
				: alertMenusTemplate),
		];
		// Update Category Level
		updatedMenus[0].cards[0].count = parsedData.category?.margin?.cnt || 0;
		updatedMenus[0].cards[0].amount =
			parsedData.category?.margin?.change || 0;
		updatedMenus[0].cards[1].count = parsedData.category?.sales?.cnt || 0;
		updatedMenus[0].cards[1].amount =
			parsedData.category?.sales?.change || 0;
		updatedMenus[0].cards[2].count =
			parsedData.category?.sellprice?.cnt || 0;
		updatedMenus[0].cards[2].amount =
			parsedData.category?.sellprice?.change || 0;
		updatedMenus[0].cards[3].count = parsedData.category?.cost?.cnt || 0;
		updatedMenus[0].cards[3].amount =
			parsedData.category?.cost?.change || 0;
		// Update Supplier Level
		updatedMenus[1].cards[0].count = parsedData.supplier?.margin?.cnt || 0;
		updatedMenus[1].cards[0].amount =
			parsedData.supplier?.margin?.change || 0;
		updatedMenus[1].cards[1].count = parsedData.supplier?.sales?.cnt || 0;
		updatedMenus[1].cards[1].amount =
			parsedData.supplier?.sales?.change || 0;
		updatedMenus[1].cards[2].count =
			parsedData.supplier?.sellprice?.cnt || 0;
		updatedMenus[1].cards[2].amount =
			parsedData.supplier?.sellprice?.change || 0;
		updatedMenus[1].cards[3].count = parsedData.supplier?.cost?.cnt || 0;
		updatedMenus[1].cards[3].amount =
			parsedData.supplier?.cost?.change || 0;
		// Update District Level
		updatedMenus[2].cards[0].count = parsedData.district?.margin?.cnt || 0;
		updatedMenus[2].cards[0].amount =
			parsedData.district?.margin?.change || 0;
		updatedMenus[2].cards[1].count = parsedData.district?.sales?.cnt || 0;
		updatedMenus[2].cards[1].amount =
			parsedData.district?.sales?.change || 0;
		updatedMenus[2].cards[2].count =
			parsedData.district?.sellprice?.cnt || 0;
		updatedMenus[2].cards[2].amount =
			parsedData.district?.sellprice?.change || 0;
		updatedMenus[2].cards[3].count = parsedData.district?.cost?.cnt || 0;
		updatedMenus[2].cards[3].amount =
			parsedData.district?.cost?.change || 0;
		setAlertMenus(updatedMenus);
	};

	return isLoading ? (
		<Loader />
	) : (
		<div className="home-wrapper">
			<MainHeader
				onVisibilityChange={setIsVisible}
				isRightVisible={isRightVisible}
				setIsRightVisible={setIsRightVisible}
				topContent={
					<>
						<>
							<div className="flex justify-between items-center w-full">
								<div className="text-left pageline">
									<span className="pagelinestart">
										Baseline and Performance
									</span>{" "}
									<span className="pagelineslash">/</span>{" "}
									<span className="pagelinesub">
										Category Alerts
									</span>
								</div>
							</div>
							<div className="execution-level-wrapper">
								<div className="execution-level-title">
									View By
								</div>
								<Segmented
									options={["At Risk", "Improved"]}
									value={
										handlePositiveNegative
											? "Improved"
											: "At Risk"
									}
									onChange={(value: any) =>
										setHandlePositiveNegative(
											value === "Improved"
										)
									}
								/>
								<div className="filter-flex border-1">
									<div className="flex justify-center items-center h-full">
										<Button
											className="bg-transparent hover:bg-transparent"
											onClick={exportDataClick}
										>
											<FileDownloadIcon
												color="black"
												className="text-fsre16"
											/>
										</Button>
									</div>
									<CSVLink
										ref={exportFullRef}
										data={
											!handlePositiveNegative
												? exportDatas
												: exportImprovedDatas
										}
										headers={csvHeaders}
										filename="category_alerts.csv"
										className="hidden"
										target="_blank"
									>
										{" "}
									</CSVLink>
								</div>
							</div>
						</>
					</>
				}
				bottomContent={
					<>
						<div className="sub-heading-text font-bold flex flex-col items-start">
							<span className="filterHead ">
								Category Manager
							</span>
							<SelectPicker
								data={managers}
								value={selectedManager}
								onChange={(value: any) =>
									setSelectedManager(value)
								}
								placeholder="Select Manager"
								cleanable={false}
							/>
						</div>
						<div className="sub-heading-text font-bold flex flex-col items-start">
							<span className="filterHead ">Project Type</span>
							<SelectPicker
								data={projectType as any}
								placeholder="All"
								value={selectedProjectType}
								defaultValue={initialProjectType as any}
								onChange={handleProjectTypeChange}
								cleanable={false}
							/>
						</div>
					</>
				}
			></MainHeader>
			<MainContent isVisible={isVisible} fullHeight={true}>
				{alertMenus.map((menu, menuIndex) => (
					<div key={menuIndex} className="alert-section">
						<div className="alert-title-wrapper">
							<div className="alert-title">{menu.title}</div>
						</div>
						<div className="cards-wrapper">
							{menu.cards.map((card, cardIndex) => (
								<div
									key={cardIndex}
									className="card"
									onClick={() =>
										handleCardClick(menuIndex, cardIndex)
									}
									style={{ cursor: "pointer" }}
								>
									<div className="card-title-wrapper">
										<div className="card-title">
											{card.title}
										</div>
									</div>
									<div className="card-details">
										<div className="card-count">
											{card.count}{" "}
											<span className="card-subtitle">
												{card.subtitle}
											</span>
										</div>
										<Divider className="vertical-divider"></Divider>
										<div className="card-amount-container">
											<div className="card-amount">
												{typeof card.amount === "number"
													? `${(
															Math.abs(
																card.amount
															) * 100
													  ).toFixed(2)}%`
													: card.amount}
												<img
													src={
														handlePositiveNegative
															? upGreenArrow
															: upRedArrow
													}
													alt="ArrowIcon"
													className={`arrow-icon ${
														handlePositiveNegative
															? ""
															: "arrow-icon-rotated"
													}`}
												/>
											</div>
											<span className="card-subamount">
												{card.subamount}
											</span>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				))}
				<div className="alert-info-text">
					*Net cost and Sell price deviation are calculated only for
					SKUs common in both years; the Sell price reduction
					calculation excludes new customer data
				</div>
			</MainContent>
			<Modal
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				className="alertModal"
			>
				<Modal.Header>
					<div className="headMain">
						<div className="headContentMain capitalise">
							{modalTitle} List
						</div>
						<div className="flex justify-end">
							<div className="sub-heading-text font-bold flex flex-col items-start">
								<span className="filterHead ">Priority</span>

								<SelectPicker
									data={priority.length ? priority : []}
									placeholder="All"
									value={selectedPriority}
									defaultValue={
										priority.length ? priority[2].value : ""
									}
									onChange={priorityHandler}
									cleanable={false}
									searchable={false}
								/>
							</div>
						</div>
					</div>
				</Modal.Header>

				<Modal.Body>
					{isLoading ? (
						<div>Loading opportunity data...</div>
					) : (
						<>
							<table
								className="common-table-ui"
								style={{
									overflowY: "unset",
									width: "fit-content",
								}}
							>
								<thead>
									<tr>
										<th className="capitalize">
											{modalTitle} Name
										</th>
										{clickedCardType === 3 && (
											<th>Spend 2025</th>
										)}
										{clickedCardType != 3 && (
											<th>Sales 2025</th>
										)}
										{clickedCardType === 0 && (
											<>
												<th>
													2026 Gross Margin (%) YTD
												</th>
												<th>2025 Gross Margin (%)</th>
												<th>
													%{" "}
													{handlePositiveNegative
														? "Increase"
														: "Drop"}
												</th>
											</>
										)}

										{clickedCardType === 1 && (
											<>
												<th>2026 Sales YTD($)</th>
												<th>2025 Sales YTD($)</th>
												<th>
													%{" "}
													{handlePositiveNegative
														? "Increase"
														: "Drop"}
												</th>
											</>
										)}

										{clickedCardType === 2 && (
											<>
												<th>
													2026 YTD Sell Price($/unit)
												</th>
												<th>2025 Sell Price($/unit)</th>
												<th>
													%{" "}
													{handlePositiveNegative
														? "Increase"
														: "Drop"}
												</th>
											</>
										)}

										{clickedCardType === 3 && (
											<>
												<th>2026 Net Cost YTD($)</th>
												<th>2025 Net Cost YTD($)</th>
												<th>% Increase</th>
											</>
										)}

										{(clickedCardType === null ||
											clickedCardType === undefined) && (
											<>
												<th>Value CY</th>
												<th>Value LY</th>
											</>
										)}
									</tr>
								</thead>
								<tbody>
									{tableData.map((row: any, index: any) => (
										<>
											{((!handlePositiveNegative &&
												row?.flag) ||
												(handlePositiveNegative &&
													!row?.flag)) && (
												<tr
													key={index}
													onClick={() =>
														handleRowClick(
															row.categoryName
														)
													}
												>
													<td>{row.categoryName}</td>
													{clickedCardType === 3 && (
														<td>
															{formatNumber(
																row.spend_2024
															)}
														</td>
													)}
													{clickedCardType != 3 && (
														<td>
															{formatNumber(
																row.sales_2024
															)}
														</td>
													)}

													{clickedCardType === 0 && (
														<>
															<td>
																{row.grossMargin2025.toFixed(
																	2
																)}
																%
															</td>
															<td>
																{row.grossMargin2024.toFixed(
																	2
																)}
																%
															</td>
															<td>
																{handlePositiveNegative
																	? Math.abs(
																			row.percentDrop
																	  ) < 0
																		? Math.abs(
																				row.percentDrop *
																					-1
																		  ).toFixed(
																				2
																		  )
																		: Math.abs(
																				row.percentDrop
																		  ).toFixed(
																				2
																		  )
																	: Math.abs(
																			row.percentDrop
																	  ).toFixed(
																			2
																	  )}
																%
															</td>
														</>
													)}

													{clickedCardType === 1 && (
														<>
															<td>
																{formatNumber(
																	row.sales2025
																)}
															</td>
															<td>
																{formatNumber(
																	row.sales2024
																)}
															</td>
															<td>
																{handlePositiveNegative
																	? row.percentDrop *
																			100 <
																	  0
																		? (
																				row.percentDrop *
																				100 *
																				-1
																		  ).toFixed(
																				2
																		  )
																		: (
																				row.percentDrop *
																				100
																		  ).toFixed(
																				2
																		  )
																	: (
																			row.percentDrop *
																			100
																	  ).toFixed(
																			2
																	  )}
																%
															</td>
														</>
													)}

													{clickedCardType === 2 && (
														<>
															<td>
																{formatNumber(
																	row.sellPrice2025
																)}
															</td>
															<td>
																{formatNumber(
																	row.sellPrice2024
																)}
															</td>
															<td>
																{handlePositiveNegative
																	? row.percentDrop *
																			100 <
																	  0
																		? (
																				row.percentDrop *
																				100 *
																				-1
																		  ).toFixed(
																				2
																		  )
																		: (
																				row.percentDrop *
																				100
																		  ).toFixed(
																				2
																		  )
																	: (
																			row.percentDrop *
																			100
																	  ).toFixed(
																			2
																	  )}
																%
															</td>
														</>
													)}

													{clickedCardType === 3 && (
														<>
															<td>
																{formatNumber(
																	row.cost2025
																)}
															</td>
															<td>
																{formatNumber(
																	row.cost2024
																)}
															</td>
															<td>
																{handlePositiveNegative
																	? row.percentIncrease *
																			100 <
																	  0
																		? (
																				row.percentIncrease *
																				100 *
																				-1
																		  ).toFixed(
																				2
																		  )
																		: (
																				row.percentIncrease *
																				100
																		  ).toFixed(
																				2
																		  )
																	: (
																			row.percentIncrease *
																			100
																	  ).toFixed(
																			2
																	  )}
																%
															</td>
														</>
													)}

													{(clickedCardType ===
														null ||
														clickedCardType ===
															undefined) && (
														<>
															<td>
																{formatNumber(
																	row.value_CY
																)}
															</td>
															<td>
																{formatNumber(
																	row.value_LY
																)}
															</td>
														</>
													)}
												</tr>
											)}
										</>
									))}
								</tbody>
							</table>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<div className="italic text-[#86bc25] text-[0.8vw] mt-1 justify-center">
						*Select a Category to see detailed Scorecard.
					</div>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

const mapDispatchToProps = (dispatch: any) => ({
	onFilterChange: (data: any) => dispatch({ type: FILTERDATA, data: data }),
});

export default connect(null, mapDispatchToProps)(Alerts);
